import {
  FilterOperator,
  ProductSearchFilters,
  ProductSearchRequest,
  SalesMetricsFilter,
  SalesSearchMetric,
} from "reedy-data/api";
import { isNonNegativeInteger } from "~/utils/helpers";

import {
  FilterType,
  NumericFilter,
  SalesDataFilter,
  SearchFilterComparisonOperator,
  SearchQuery,
  SearchQueryFilters,
  SearchType,
  ValidAgeRangeFilterSchema,
} from "~/hooks/search/types";
import {
  DEFAULT_PAGE_SIZE,
  HYBRID_DEFAULT_PAGE_SIZE,
  SEMANTIC_SEARCH_PAGE_SIZE,
  parseInputDate,
  parseToUTCDate,
} from "~/hooks/search/constants";
import {
  getActiveSalesDataMetrics,
  isActiveSalesDataFilter,
  type ActiveSalesDataMetric,
} from "~/hooks/search/sales-data-filter-utils";

const MAX_AGGREGATION_SIZE = 1000;

const FACET_FIELD_MAP: Record<string, string> = {
  publisher: "publishers",
};

function mapFacetField(field: string): string {
  return FACET_FIELD_MAP[field] ?? field;
}

function mapComparisonOperator(
  operator: SearchFilterComparisonOperator,
): FilterOperator {
  switch (operator) {
    case SearchFilterComparisonOperator.GREATER:
      return FilterOperator.GT;
    case SearchFilterComparisonOperator.LESS:
      return FilterOperator.LT;
    case SearchFilterComparisonOperator.EQUALS:
      return FilterOperator.EQ;
    default:
      throw new Error(`Unhandled operator type: ${operator}`);
  }
}

function buildNumericFilter(
  filter: NumericFilter | null | undefined,
): { operator: FilterOperator; value: number } | undefined {
  if (
    filter?.type === FilterType.NUMERIC &&
    isNonNegativeInteger(filter.value) &&
    filter.comparisonOperator
  ) {
    return {
      operator: mapComparisonOperator(filter.comparisonOperator),
      value: filter.value,
    };
  }
  return undefined;
}

function mapSalesDataMetric(metric: ActiveSalesDataMetric): SalesSearchMetric {
  switch (metric.key) {
    case "totalUnits":
      return SalesSearchMetric.TOTAL_UNITS;
    case "amazonOrderedUnits":
      return SalesSearchMetric.AMAZON_ORDERED_UNITS;
    case "amazonConversionRate":
      return SalesSearchMetric.AMAZON_CONVERSION_RATE;
    default:
      throw new Error(`Unhandled sales data metric: ${metric.key}`);
  }
}

function buildSalesMetricsFilter(
  filter: SalesDataFilter | null | undefined,
): SalesMetricsFilter | undefined {
  const salesDataMaxDate = new Date();
  if (!isActiveSalesDataFilter(filter, salesDataMaxDate)) {
    return undefined;
  }
  const activeMetrics = getActiveSalesDataMetrics(filter);

  const result: SalesMetricsFilter = {
    criteria: activeMetrics.map((metric) => ({
      metric: mapSalesDataMetric(metric),
      operator: mapComparisonOperator(metric.comparisonOperator),
      value: metric.definition.percentage ? metric.value / 100 : metric.value,
    })),
  };

  if (filter.range.from && filter.range.to) {
    result.start = parseToUTCDate(filter.range.from, false);
    result.end = parseToUTCDate(filter.range.to, false);
  }

  return result;
}

function buildElasticsearchFilters(
  filters?: SearchQueryFilters,
): ProductSearchFilters | undefined {
  if (!filters) {
    return undefined;
  }

  const result: ProductSearchFilters = {};

  const keywordCount = buildNumericFilter(filters.keywordCount);
  if (keywordCount) result.keyword_count = keywordCount;

  const descriptionLength = buildNumericFilter(filters.descriptionLength);
  if (descriptionLength) result.description_length = descriptionLength;

  const descriptionWordCount = buildNumericFilter(filters.descriptionWordCount);
  if (descriptionWordCount) {
    result.description_word_count = descriptionWordCount;
  }

  const themaCodeCount = buildNumericFilter(filters.themaCodeCount);
  if (themaCodeCount) result.thema_code_count = themaCodeCount;

  const bisacCodeCount = buildNumericFilter(filters.bisacCodeCount);
  if (bisacCodeCount) result.bisac_code_count = bisacCodeCount;

  const publishedAtFilter = filters.publishedAt;
  // Drop half-typed date ranges so the live count preview does not throw.
  if (
    publishedAtFilter?.range?.from &&
    publishedAtFilter?.range?.to &&
    parseInputDate(publishedAtFilter.range.from) !== undefined &&
    parseInputDate(publishedAtFilter.range.to) !== undefined
  ) {
    result.published_at = {
      gte: parseToUTCDate(publishedAtFilter.range.from, false),
      lte: parseToUTCDate(publishedAtFilter.range.to, true),
    };
  }

  const publishedByFilter = filters.publisher;
  if (publishedByFilter?.values?.length) {
    result.published_by = {
      publishers: publishedByFilter.values,
    };
  }

  const imprintFilter = filters.imprints;
  if (imprintFilter?.values?.length) {
    result.imprint = {
      imprints: imprintFilter.values,
    };
  }

  const productGroupDescriptionFilter = filters.productGroupDescription;
  if (productGroupDescriptionFilter?.values?.length) {
    result.product_group_description = {
      productGroupDescriptions: productGroupDescriptionFilter.values,
    };
  }

  const authorFilter = filters.author;
  if (authorFilter?.values?.length) {
    result.author = {
      authors: authorFilter.values,
    };
  }

  const genreFilter = filters.genre;
  if (genreFilter?.values?.length) {
    result.genre = {
      genres: genreFilter.values,
    };
  }

  const keywordsFilter = filters.keywords;
  if (keywordsFilter?.values?.length) {
    result.keywords = {
      keywords: keywordsFilter.values,
    };
  }

  const themaCodesFilter = filters.themaCodes;
  if (themaCodesFilter?.values?.length) {
    result.thema_codes = {
      codes: themaCodesFilter.values,
    };
  }

  const bisacCodesFilter = filters.bisacCodes;
  if (bisacCodesFilter?.values?.length) {
    result.bisac_codes = {
      codes: bisacCodesFilter.values,
    };
  }

  const ageRangeFilter = filters.ageRange;
  const parsedAgeRange = ageRangeFilter
    ? ValidAgeRangeFilterSchema.safeParse(ageRangeFilter)
    : undefined;
  if (parsedAgeRange?.success) {
    const { ageType, range } = parsedAgeRange.data;
    result.age_range = {
      age_type: ageType,
      gte: range.min,
      lte: range.max,
    };
  }

  const salesMetricsFilter = buildSalesMetricsFilter(filters.salesData);
  if (salesMetricsFilter) result.sales_metrics = salesMetricsFilter;

  return Object.keys(result).length > 0 ? result : undefined;
}

function buildAggregations(query: Partial<SearchQuery>) {
  if (!query.facets?.length) return {};

  const allFields = query.facets
    .map((f) => mapFacetField(f.trim()))
    .filter(Boolean);

  if (!allFields.length) return {};

  const facetSearchText = query.facetSearch?.text || undefined;
  const facetSearchField =
    query.facetSearch && facetSearchText
      ? mapFacetField(query.facetSearch.field)
      : undefined;

  const aggregations =
    facetSearchField && allFields.includes(facetSearchField)
      ? [facetSearchField]
      : allFields;

  return {
    aggregations,
    ...(facetSearchText && { aggregation_query: facetSearchText }),
    ...(query.facetSize != null && {
      aggregation_size: Math.min(
        Math.max(1, query.facetSize),
        MAX_AGGREGATION_SIZE,
      ),
    }),
  };
}

interface BuildElasticsearchRequestOptions {
  hybridSearchEnabled?: boolean;
  includeAgeRangeBounds?: boolean;
}

export const buildElasticsearchRequest = (
  query: Partial<SearchQuery>,
  options: BuildElasticsearchRequestOptions = {},
): ProductSearchRequest => {
  const useSemanticSearch = query.type === SearchType.SEMANTIC;
  const useHybridSearch =
    query.type === SearchType.HYBRID && (options.hybridSearchEnabled ?? true);
  // Age bounds apply only on keyword/browse, not semantic/hybrid/ISBN.
  const ageRangeBounds =
    options.includeAgeRangeBounds && !useSemanticSearch && !useHybridSearch
      ? { include_age_range_bounds: true }
      : {};
  // Browse sends ""; ISBN keeps "*" because lookup uses the isbns filter.
  const queryString = query.query?.trim() ?? "";
  const filters = buildElasticsearchFilters(query.filters);

  const resolvePerPage = (): number => {
    if (query.pageSize === 0) return 0;
    if (useSemanticSearch) return SEMANTIC_SEARCH_PAGE_SIZE;
    return query.pageSize ?? DEFAULT_PAGE_SIZE;
  };

  if (query.type === SearchType.ISBN) {
    const isbns = (query.query || "")
      .split(",")
      .map((s) => s.trim())
      .filter((id) => id.length === 13 && /^\d+$/.test(id));

    return {
      query: "*",
      use_semantic: false,
      page: query.page ?? 1,
      per_page: query.pageSize ?? DEFAULT_PAGE_SIZE,
      ...(isbns.length > 0 && { filters: { ...filters, isbns: { isbns } } }),
      ...buildAggregations(query),
    };
  }

  if (useHybridSearch) {
    return {
      query: queryString,
      use_semantic: false,
      use_hybrid: true,
      page: query.page ?? 1,
      per_page: query.pageSize ?? HYBRID_DEFAULT_PAGE_SIZE,
      ...(filters && { filters }),
      ...buildAggregations(query),
    };
  }

  return {
    query: queryString,
    use_semantic: useSemanticSearch,
    page: useSemanticSearch ? 1 : (query.page ?? 1),
    per_page: resolvePerPage(),
    ...(filters && { filters }),
    ...buildAggregations(query),
    ...ageRangeBounds,
  };
};
